// @ts-check

/**
 * @file Registers frontend model classes with Scoundrel for browser system tests.
 */

import {Account} from "../frontend-models/account"
import {BoardColumn} from "../frontend-models/board-column"
import {Board} from "../frontend-models/board"
import {Comment} from "../frontend-models/comment"
import {InvoiceGroupLink} from "../frontend-models/invoice-group-link"
import {InvoiceGroup} from "../frontend-models/invoice-group"
import {InvoiceLine} from "../frontend-models/invoice-line"
import {Invoice} from "../frontend-models/invoice"
import {Organization} from "../frontend-models/organization"
import {Project} from "../frontend-models/project"
import {TaskBoard} from "../frontend-models/task-board"
import {TaskAssignedUser} from "../frontend-models/task-assigned-user"
import {TaskType} from "../frontend-models/task-type"
import {Task} from "../frontend-models/task"
import {Timelog} from "../frontend-models/timelog"
import {UploadedFile} from "../frontend-models/uploaded-file"
import {User} from "../frontend-models/user"
import {UserRole} from "../frontend-models/user-role"
import LocalReplicaDatabaseProbe from "./local-replica-database-probe"
import BoardSubscriptionProbe from "./board-subscription-probe"

/** @import SystemTestBrowserHelper from "system-testing/build/system-test-browser-helper.js" */

/** @type {WeakSet<object>} */
const registeredScoundrels = new WeakSet()

/**
 * Registers backend-backed frontend model classes for system-test fixture setup.
 * @param {SystemTestBrowserHelper} browserHelper Browser helper that owns Scoundrel.
 * @returns {Promise<void>} Resolves after classes are registered.
 */
export default async function registerBackendModelClassesForSystemTests(browserHelper) {
  await browserHelper.waitForScoundrelStarted()

  registerBackendModelClassesWithScoundrel(browserHelper.getScoundrel())
}

/**
 * Registers model classes once on one exact Scoundrel client.
 * @param {ReturnType<SystemTestBrowserHelper["getScoundrel"]>} scoundrel Connected client.
 * @returns {void} Nothing.
 */
export function registerBackendModelClassesWithScoundrel(scoundrel) {
  if (registeredScoundrels.has(scoundrel)) return

  scoundrel.registerClass("Account", Account)
  scoundrel.registerClass("Board", Board)
  scoundrel.registerClass("BoardColumn", BoardColumn)
  scoundrel.registerClass("Comment", Comment)
  scoundrel.registerClass("InvoiceGroup", InvoiceGroup)
  scoundrel.registerClass("InvoiceGroupLink", InvoiceGroupLink)
  scoundrel.registerClass("Invoice", Invoice)
  scoundrel.registerClass("InvoiceLine", InvoiceLine)
  scoundrel.registerClass("Organization", Organization)
  scoundrel.registerClass("Project", Project)
  scoundrel.registerClass("Task", Task)
  scoundrel.registerClass("TaskBoard", TaskBoard)
  scoundrel.registerClass("TaskAssignedUser", TaskAssignedUser)
  scoundrel.registerClass("TaskType", TaskType)
  scoundrel.registerClass("Timelog", Timelog)
  scoundrel.registerClass("UploadedFile", UploadedFile)
  scoundrel.registerClass("User", User)
  scoundrel.registerClass("UserRole", UserRole)
  scoundrel.registerClass("LocalReplicaDatabaseProbe", LocalReplicaDatabaseProbe)
  scoundrel.registerClass("BoardSubscriptionProbe", BoardSubscriptionProbe)
  registeredScoundrels.add(scoundrel)
}
